import React, { useState } from 'react';
import { ArchiveIcon, BellIcon, BookOpenIcon, ChevronRightIcon, ClockIcon, FilePlusIcon, FolderIcon, FolderPlusIcon, HashIcon, KeyRoundIcon, PencilIcon, PinIcon, StickyNoteIcon, Trash2Icon, UsersIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useNotes } from '../../contexts/NotesContext';
import { folderDepth } from '../../utils/notes';
import { PinSetupDialog } from './PinSetupDialog';
import { PinResetDialog } from './PinResetDialog';
import type { NotesSelection, NotesView } from '../../types/notes';
interface NotesSidebarProps {
  selection: NotesSelection;
  onSelect: (selection: NotesSelection) => void;
  onNewNote: () => void;
}
const VIEWS: {
  view: NotesView;
  label: string;
  icon: typeof StickyNoteIcon;
}[] = [{
  view: 'all',
  label: 'All notes',
  icon: StickyNoteIcon
}, {
  view: 'recent',
  label: 'Recent',
  icon: ClockIcon
}, {
  view: 'pinned',
  label: 'Pinned',
  icon: PinIcon
}, {
  view: 'shared',
  label: 'Shared with me',
  icon: UsersIcon
}, {
  view: 'diary',
  label: 'My Diary',
  icon: BookOpenIcon
}];
export function NotesSidebar({
  selection,
  onSelect,
  onNewNote
}: NotesSidebarProps) {
  const {
    ownNotes,
    sharedWithMe,
    folders,
    allTags,
    notes,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFolder,
    lockCredential,
    unlockedNotes,
    lockAllNotes
  } = useNotes();
  const [creatingIn, setCreatingIn] = useState<string | null | undefined>(undefined);
  const [draftName, setDraftName] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [setupMode, setSetupMode] = useState<'create' | 'change' | 'remove' | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const lockedCount = ownNotes.filter(note => note.locked).length;
  const active = ownNotes.filter(note => note.lifecycle === 'active');
  const counts: Record<string, number> = {
    all: active.length,
    recent: active.length,
    pinned: active.filter(note => note.pinned).length,
    shared: sharedWithMe.length,
    diary: active.filter(note => note.diaryDate).length,
    archive: ownNotes.filter(note => note.lifecycle === 'archived').length,
    trash: ownNotes.filter(note => note.lifecycle === 'trashed').length
  };
  const reminders = active.filter(note => note.reminder).sort((a, b) => a.reminder!.sortKey < b.reminder!.sortKey ? -1 : 1).slice(0, 2);
  function rowClasses(isActive: boolean) {
    return `flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] font-medium transition-colors duration-150 ease-out ${isActive ? 'bg-brand-50 text-brand-800' : 'text-ink-muted hover:bg-canvas hover:text-ink'}`;
  }
  function submitFolder() {
    if (draftName.trim()) createFolder(draftName, creatingIn ?? null);
    setDraftName('');
    setCreatingIn(undefined);
  }
  return <div className="flex h-full flex-col" data-id="element-3797" data-mp="1pqsgn4106jru">
      <div className="p-3" data-id="element-3798" data-mp="1prjy7axrmgz4">
        <Button variant="primary" className="w-full" icon={<FilePlusIcon className="h-4 w-4" data-id="element-3800" data-mp="3ael2kctuqzwi" />} onClick={onNewNote} data-id="element-3799" data-mp="3ago33qsmsbfh">
          New note
        </Button>
      </div>

      <nav aria-label="Notes views" className="px-2" data-id="element-3801" data-mp="3k67iunl1rp64">
        <ul data-id="element-3802" data-mp="3k5o1b7zb72sm">
          {VIEWS.map(item => {
          const Icon = item.icon;
          const isActive = selection.view === item.view;
          return <li key={item.view} data-id="element-3803" data-mp="2riuymk2cztfz">
                <button type="button" onClick={() => onSelect({
              view: item.view
            })} aria-current={isActive} className={rowClasses(isActive)} data-id="element-3804" data-mp="2ri2026dk0a8x">
                  <Icon className="h-4 w-4 shrink-0" data-id="element-3805" data-mp="2wh4ukwvwqy2n" />
                  <span className="flex-1 truncate" data-id="element-3806" data-mp="2wfbobip7ifv6">{item.label}</span>
                  <span className="cz-num text-[12px] text-ink-subtle" data-id="element-3807" data-mp="frhhet0xw5p8">{counts[item.view]}</span>
                </button>
              </li>;
        })}
        </ul>
      </nav>

      <div className="mt-4 px-2" data-id="element-3808" data-mp="171c7v1bvp16o">
        <div className="flex items-center justify-between px-2.5" data-id="element-3809" data-mp="172ez5ad455n4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3810" data-mp="1b1ntagc2f5j7">Folders</p>
          <button type="button" aria-label="New top-level folder" onClick={() => {
          setCreatingIn(null);
          setDraftName('');
        }} className="rounded p-1 text-ink-subtle hover:bg-canvas hover:text-ink" data-id="element-3811" data-mp="1g1935lsrwyap">
            <FolderPlusIcon className="h-3.5 w-3.5" data-id="element-3812" data-mp="1u0i8fqffy7zw" />
          </button>
        </div>
        <ul className="mt-1" data-id="element-3813" data-mp="1ysa3oe9jl1wd">
          {folders.map(folder => {
          const depth = folderDepth(folder.id, folders);
          const isActive = selection.view === 'folder' && selection.folderId === folder.id;
          const count = notes.filter(note => note.folderId === folder.id && note.lifecycle === 'active').length;
          return <li key={folder.id} data-id="element-3814" data-mp="21xllf2hpovr">
                <div className="group flex items-center" data-id="element-3815" data-mp="22yvhg7rs5bw">
                  {renaming === folder.id ? <input autoFocus defaultValue={folder.name} onBlur={event => {
                renameFolder(folder.id, event.target.value.trim() || folder.name);
                setRenaming(null);
              }} onKeyDown={event => {
                if (event.key === 'Enter') event.currentTarget.blur();
                if (event.key === 'Escape') setRenaming(null);
              }} className="mx-2.5 my-1 w-full rounded border border-brand-700 px-2 py-1 text-[13px] text-ink focus:outline-none" style={{
                marginLeft: 10 + depth * 12
              }} aria-label="Folder name" data-id="element-3816" data-mp="3t7fgocgd8460" /> : <>
                      <button type="button" onClick={() => onSelect({
                  view: 'folder',
                  folderId: folder.id
                })} aria-current={isActive} className={rowClasses(isActive)} style={{
                  paddingLeft: 10 + depth * 12
                }} data-id="element-3817" data-mp="yqd9l6pybq6r">
                        <FolderIcon className="h-4 w-4 shrink-0" data-id="element-3818" data-mp="2o8a9vjuebgmj" />
                        <span className="flex-1 truncate" data-id="element-3819" data-mp="2a9smjn8ek655">{folder.name}</span>
                        <span className="cz-num text-[12px] text-ink-subtle" data-id="element-3820" data-mp="2abwzeuoa4fhk">{count || ''}</span>
                      </button>
                      <span className="flex shrink-0 opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100" data-id="element-3821" data-mp="20bwdr22xtna7">
                        <button type="button" aria-label={`New subfolder in ${folder.name}`} onClick={() => {
                    setCreatingIn(folder.id);
                    setDraftName('');
                  }} className="rounded p-1 text-ink-subtle hover:text-ink" data-id="element-3822" data-mp="25b91rq5jp0ed">
                          <FolderPlusIcon className="h-3.5 w-3.5" data-id="element-3823" data-mp="1j3vdz550rccc" />
                        </button>
                        <button type="button" aria-label={`Rename ${folder.name}`} onClick={() => setRenaming(folder.id)} className="rounded p-1 text-ink-subtle hover:text-ink" data-id="element-3824" data-mp="1xt1ecwflf8lt">
                          <PencilIcon className="h-3.5 w-3.5" data-id="element-3825" data-mp="21umq2ege5guf" />
                        </button>
                        {folder.parentId && <button type="button" aria-label={`Move ${folder.name} to top level`} onClick={() => moveFolder(folder.id, null)} className="rounded p-1 text-ink-subtle hover:text-ink" data-id="element-3826" data-mp="2bqg9mhsj2alp">
                            <ChevronRightIcon className="h-3.5 w-3.5 rotate-180" data-id="element-3827" data-mp="3ronb1e097zsg" />
                          </button>}
                        <button type="button" aria-label={`Delete ${folder.name}`} onClick={() => deleteFolder(folder.id)} className="rounded p-1 text-ink-subtle hover:text-danger-700" data-id="element-3828" data-mp="59ksd3ejxuqf">
                          <Trash2Icon className="h-3.5 w-3.5" data-id="element-3829" data-mp="3docjs9v18j6r" />
                        </button>
                      </span>
                    </>}
                </div>
                {creatingIn === folder.id && <input autoFocus value={draftName} onChange={event => setDraftName(event.target.value)} onBlur={submitFolder} onKeyDown={event => {
              if (event.key === 'Enter') submitFolder();
              if (event.key === 'Escape') setCreatingIn(undefined);
            }} placeholder="Subfolder name" aria-label="New subfolder name" className="my-1 w-full rounded border border-brand-700 px-2 py-1 text-[13px] text-ink focus:outline-none" style={{
              marginLeft: 10 + (depth + 1) * 12
            }} data-id="element-3830" data-mp="3mrdoq785w48u" />}
              </li>;
        })}
        </ul>
        {creatingIn === null && <input autoFocus value={draftName} onChange={event => setDraftName(event.target.value)} onBlur={submitFolder} onKeyDown={event => {
        if (event.key === 'Enter') submitFolder();
        if (event.key === 'Escape') setCreatingIn(undefined);
      }} placeholder="Folder name" aria-label="New folder name" className="mx-2.5 mt-1 w-[calc(100%-1.25rem)] rounded border border-brand-700 px-2 py-1 text-[13px] text-ink focus:outline-none" data-id="element-3831" data-mp="1uqgc4wj7o6h3" />}
      </div>

      {allTags.length > 0 && <div className="mt-4 px-2" data-id="element-3832" data-mp="1mbi0369r62il">
          <p className="px-2.5 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3833" data-mp="1maglwsxfwzwn">
            Tags
          </p>
          <ul className="mt-1 flex flex-wrap gap-1.5 px-2.5" data-id="element-3834" data-mp="tmkhf21mq3pb">
            {allTags.map(tag => {
          const isActive = selection.view === 'tag' && selection.tag === tag;
          const count = notes.filter(note => note.tags.includes(tag) && note.lifecycle === 'active').length;
          return <li key={tag} data-id="element-3835" data-mp="ykvsfg3nuk94">
                  <button type="button" onClick={() => onSelect({
              view: 'tag',
              tag
            })} aria-current={isActive} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[12px] font-medium transition-colors duration-150 ease-out ${isActive ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line text-ink-muted hover:border-ink-subtle hover:text-ink'}`} data-id="element-3836" data-mp="yjuen3v9fjnp">
                    <HashIcon className="h-3 w-3" data-id="element-3837" data-mp="2dj9tl2oeqofh" />
                    {tag}
                    <span className="cz-num text-ink-subtle" data-id="element-3838" data-mp="319duv3e4ic3b">{count}</span>
                  </button>
                </li>;
        })}
          </ul>
        </div>}

      {reminders.length > 0 && <div className="mt-4 px-2" data-id="element-3839" data-mp="2r9mw2pij5axf">
          <p className="px-2.5 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3840" data-mp="2w999ayycyr68">
            Upcoming reminders
          </p>
          <ul className="mt-1" data-id="element-3841" data-mp="1fu827ulmaz6j">
            {reminders.map(note => <li key={note.id} data-id="element-3842" data-mp="1kum0wm1bwwrw">
                <button type="button" onClick={() => onSelect({
            view: 'all'
          })} className="flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-canvas" data-id="element-3843" data-mp="1kvno1ovmd9pz">
                  <BellIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-3844" data-mp="rzo6crzuqklq" />
                  <span className="min-w-0" data-id="element-3845" data-mp="rxbhduvof9z6">
                    <span className="block truncate text-[13px] font-medium text-ink" data-id="element-3846" data-mp="mzibl2yachzf">
                      {note.title}
                    </span>
                    <span className="cz-num block text-[12px] text-ink-subtle" data-id="element-3847" data-mp="hz41lfd4ixyr">
                      {note.reminder?.dateLabel} · {note.reminder?.timeLabel}
                    </span>
                  </span>
                </button>
              </li>)}
          </ul>
        </div>}

      <div className="mt-auto border-t border-line p-2" data-id="element-3848" data-mp="441yezznvmpn">
        <ul data-id="element-3849" data-mp="23di8s65qxtiz">
          {[{
          view: 'archive' as NotesView,
          label: 'Archive',
          icon: ArchiveIcon
        }, {
          view: 'trash' as NotesView,
          label: 'Trash',
          icon: Trash2Icon
        }].map(item => {
          const Icon = item.icon;
          const isActive = selection.view === item.view;
          return <li key={item.view} data-id="element-3850" data-mp="2fpas8mdqe583">
                <button type="button" onClick={() => onSelect({
              view: item.view
            })} aria-current={isActive} className={rowClasses(isActive)} data-id="element-3851" data-mp="2fqdj4ub207o0">
                  <Icon className="h-4 w-4 shrink-0" data-id="element-3852" data-mp="1n2hnzu14untc" />
                  <span className="flex-1 truncate" data-id="element-3853" data-mp="1s0j7uh6dur5v">{item.label}</span>
                  <span className="cz-num text-[12px] text-ink-subtle" data-id="element-3854" data-mp="1wyt4kw7r9ztl">{counts[item.view]}</span>
                </button>
              </li>;
        })}
        </ul>

        <div className="mt-1 border-t border-line px-3 pb-1 pt-2.5" data-id="element-3855" data-mp="20xtluaal4ueh">
          <p className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3856" data-mp="38d6nrnrkdr3a">
            <KeyRoundIcon className="h-3.5 w-3.5" data-id="element-3857" data-mp="3chef7spepl57" /> Notes lock
          </p>
          {lockCredential ? <>
              <p className="mt-1 text-[12px] leading-5 text-ink-muted" data-id="element-3858" data-mp="35zba7eyehqah">
                PIN set {lockCredential.setOn.split(',')[0]}.{' '}
                {lockedCount === 0 ? 'No notes locked yet.' : `${lockedCount} note${lockedCount === 1 ? '' : 's'} locked.`}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[12px] font-medium" data-id="element-3859" data-mp="3twigl7o10r9p">
                <button type="button" onClick={() => setSetupMode('change')} className="text-brand-800 hover:underline" data-id="element-3860" data-mp="3oygwqkis0nx6">
                  Change PIN
                </button>
                <button type="button" onClick={() => setSetupMode('remove')} className="text-brand-800 hover:underline" data-id="element-3861" data-mp="2j4im6t9fji2">
                  Remove PIN
                </button>
                {unlockedNotes.length > 0 && <button type="button" onClick={lockAllNotes} className="text-brand-800 hover:underline" data-id="element-3862" data-mp="v5cbfvbnz3gp">
                    Lock now
                  </button>}
              </div>
            </> : <>
              <p className="mt-1 text-[12px] leading-5 text-ink-muted" data-id="element-3863" data-mp="r526mlzlyj3f">
                Set a PIN to lock individual notes on this device.
              </p>
              <button type="button" onClick={() => setSetupMode('create')} className="mt-1.5 text-[12px] font-medium text-brand-800 hover:underline" data-id="element-3864" data-mp="h5tgy5brzqmv">
                Set a Notes PIN
              </button>
            </>}
        </div>
      </div>

      <PinSetupDialog open={setupMode !== null} mode={setupMode ?? 'create'} onClose={() => setSetupMode(null)} onForgot={() => {
      setSetupMode(null);
      setResetOpen(true);
    }} data-id="element-3865" data-mp="3plmxgk63x23x" />
      <PinResetDialog open={resetOpen} onClose={() => setResetOpen(false)} data-id="element-3866" data-mp="1vd9cc1viinrr" />
    </div>;
}